import React from 'react';

// ─── BADGE ────────────────────────────────────────────────────────────────────
export const Badge = ({ type = 'neutral', children }) => (
  <span className={`badge b-${type}`}>
    <span className="bdot" />
    {children}
  </span>
);

// ─── CHIP ────────────────────────────────────────────────────────────────────
export const Chip = ({ type = 'up', children }) => (
  <span className={`chip ${type}`}>{children}</span>
);

// ─── AVATAR ──────────────────────────────────────────────────────────────────
export const Avatar = ({ initials, color = '#24695c', size = 28 }) => (
  <div className="avatar" style={{ background: color, width: size, height: size, fontSize: size * 0.36 }}>
    {initials}
  </div>
);

// ─── PROGRESS BAR ────────────────────────────────────────────────────────────
export const ProgressBar = ({ value, max = 100, variant = '', label, sublabel }) => (
  <div className="prog-wrap">
    {(label || sublabel) && (
      <div className="prog-label">
        <span>{label}</span>
        <span>{sublabel}</span>
      </div>
    )}
    <div className="prog-track">
      <div className={`prog-fill ${variant}`} style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
    </div>
  </div>
);

// ─── SPLIT BAR ───────────────────────────────────────────────────────────────
export const SplitBar = ({ legacyPct = 0, instPct = 0, label = true }) => (
  <div>
    <div className="split-bar">
      {legacyPct > 0 && <div className="sb-l" style={{ width: `${legacyPct}%` }} />}
      {instPct > 0  && <div className="sb-i" style={{ width: `${instPct}%` }}  />}
    </div>
    {label && (
      <div style={{ fontSize: 10, color: 'var(--text3)' }}>
        {legacyPct > 0 && `Legacy ${legacyPct}%`}
        {legacyPct > 0 && instPct > 0 && ' / '}
        {instPct > 0   && `Inst ${instPct}%`}
        {instPct === 0 && legacyPct > 0 && ' only'}
      </div>
    )}
  </div>
);

// ─── KPI CARD ────────────────────────────────────────────────────────────────
export const KpiCard = ({ label, value, sub, chip, chipType, highlight, highlightColor }) => (
  <div className={`kpi-card${highlight ? (highlightColor === 'accent' ? ' hl-w' : ' hl') : ''}`}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-val">{value}</div>
    {sub  && <div className="kpi-sub">{sub}</div>}
    {chip && <Chip type={chipType || 'up'}>{chip}</Chip>}
  </div>
);

// ─── STAT BOX ────────────────────────────────────────────────────────────────
export const StatBox = ({ label, value, sub, accentLeft, accentColor }) => (
  <div className="stat-box" style={accentLeft ? { borderLeft: `3px solid ${accentColor || 'var(--brand)'}` } : {}}>
    <div className="stat-label">{label}</div>
    <div className="stat-val">{value}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

// ─── CARD WRAPPER ────────────────────────────────────────────────────────────
export const Card = ({ children, className = '' }) => (
  <div className={`card ${className}`}>{children}</div>
);

// ─── CARD HEAD ───────────────────────────────────────────────────────────────
export const CardHead = ({ title, sub, action, actionLabel = 'View all →', onAction }) => (
  <div className="card-head">
    <div>
      <div className="card-title">{title}</div>
      {sub && <div className="card-sub">{sub}</div>}
    </div>
    {action && <button className="card-action" onClick={onAction}>{actionLabel}</button>}
  </div>
);

// ─── TABS ────────────────────────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div className="card-tabs">
    {tabs.map(t => (
      <button key={t} className={`ctab ${active === t ? 'active' : ''}`} onClick={() => onChange(t)}>
        {t}
      </button>
    ))}
  </div>
);

// ─── FILTER BAR ──────────────────────────────────────────────────────────────
export const FilterBar = ({ children }) => (
  <div className="fbar">{children}</div>
);

// ─── MODAL ───────────────────────────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, width = 540 }) => {
  if (!open) return null;
  return (
    <div
      className="modal-overlay open"
      onClick={e => e.target === e.currentTarget && onClose?.()}
    >
      <div className="modal" style={{ width, maxWidth: 'calc(100vw - 32px)' }}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          {onClose && (
            <button type="button" className="modal-close" onClick={onClose}>✕</button>
          )}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

// ─── DRAWER ──────────────────────────────────────────────────────────────────
export const Drawer = ({ open, onClose, title, children }) => (
  <>
    <div className={`drawer-overlay ${open ? 'open' : ''}`} onClick={onClose} />
    <div className={`drawer ${open ? 'open' : ''}`}>
      <div className="drawer-head">
        <div className="drawer-title">{title}</div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="drawer-body">{children}</div>
    </div>
  </>
);

// ─── TOGGLE ──────────────────────────────────────────────────────────────────
export const Toggle = ({ on, onChange }) => (
  <div className={`toggle ${on ? '' : 'off'}`} onClick={() => onChange && onChange(!on)}>
    <div className="toggle-knob" />
  </div>
);

// ─── INFO ROW ────────────────────────────────────────────────────────────────
export const InfoRow = ({ label, value }) => (
  <div className="info-row">
    <span className="info-key">{label}</span>
    <span className="info-val">{value}</span>
  </div>
);

// ─── SECTION LABEL ───────────────────────────────────────────────────────────
export const SectionLabel = ({ children, style }) => (
  <div className="section-label" style={style}>{children}</div>
);

// ─── TIMELINE ────────────────────────────────────────────────────────────────
export const Timeline = ({ items }) => (
  <div className="tl">
    {items.map((item, i) => (
      <div className="tl-item" key={i}>
        <div className="tl-dot" style={item.color ? { background: item.color } : {}} />
        <div className="tl-line" />
        <div className="tl-body">
          <div className="tl-title">{item.title}</div>
          <div className="tl-date">{item.date}</div>
        </div>
      </div>
    ))}
  </div>
);

// ─── DONUT CHART (SVG) ───────────────────────────────────────────────────────
export const DonutChart = ({ legacy, institutional }) => {
  const total = legacy + institutional;
  const legacyPct = total > 0 ? (legacy / total) * 100 : 50;
  const instPct = 100 - legacyPct;
  const circ = 2 * Math.PI * 26;
  const legacyDash = (legacyPct / 100) * circ;
  const instDash   = (instPct / 100) * circ;

  return (
    <div className="donut-row">
      <svg width="76" height="76" viewBox="0 0 76 76">
        <circle cx="38" cy="38" r="26" fill="none" stroke="#e0e8e6" strokeWidth="14" />
        <circle cx="38" cy="38" r="26" fill="none" stroke="#24695c" strokeWidth="14"
          strokeDasharray={`${legacyDash} ${circ - legacyDash}`}
          strokeDashoffset="0" transform="rotate(-90 38 38)" />
        <circle cx="38" cy="38" r="26" fill="none" stroke="#f0a500" strokeWidth="14"
          strokeDasharray={`${instDash} ${circ - instDash}`}
          strokeDashoffset={`-${legacyDash}`} transform="rotate(-90 38 38)" />
        <text x="38" y="42" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1a2421">
          {Math.round(legacyPct)}%
        </text>
      </svg>
      <div className="dleg">
        <div className="dleg-item">
          <div className="dleg-dot" style={{ background: 'var(--brand)' }} />
          <span className="dleg-lbl">Legacy pool</span>
          <span className="dleg-val" style={{ marginLeft: 8 }}>
            R {Math.round(legacy / 1000000 * 10) / 10}M
          </span>
        </div>
        <div className="dleg-item">
          <div className="dleg-dot" style={{ background: 'var(--accent)' }} />
          <span className="dleg-lbl">Institutional</span>
          <span className="dleg-val" style={{ marginLeft: 8 }}>
            R {Math.round(institutional / 1000000 * 10) / 10}M
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── BAR CHART (custom) ──────────────────────────────────────────────────────
export const BarChart = ({ rows }) => (
  <div className="bar-chart">
    {rows.map((row, i) => (
      <div className="bc-row" key={i}>
        <div className="bc-label">{row.label}</div>
        <div className="bc-track">
          <div className="bc-fill" style={{ width: `${row.pct}%`, background: row.color || 'var(--brand)' }} />
        </div>
        <div className="bc-val">{row.val}</div>
      </div>
    ))}
  </div>
);
